import React, { useEffect, useMemo, useRef, useState } from "react";
import "react-phone-number-input/style.css";
import {
  CButton,
  CCard,
  CCardBody,
  CCardGroup,
  CCol,
  CForm,
  CFormInput,
  CImage,
  CRow,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from "@coreui/react";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import CustomInput from "@/components/CustomInput";
import {
  useEditProfile,
  useGetProfile,
  useSendEmailVerificationOtp,
  useVerifyEmailOtp,
  useLogout,
} from "@/hooks";
import { useDispatch } from "react-redux";
import { imagePreviewFromik } from "@/helpers/common";
import logo from "@/assets/images/avatars/DefaultImg.png";
import { toast } from "react-toastify";
import validationHelper from "@/helpers/validationHelper";
import CIcon from "@coreui/icons-react";
import { cilPencil } from "@coreui/icons";
import { changePassword, dashboard, signIn } from "@/libs/routes";
import { updateUserData, loggedUser } from "@/store/features/userSlice";
import TabNavigation from "@/components/TabNavigation";
import { useNavigate } from "react-router-dom";
import { useTranslationContext } from "@/context/TranslationContext";
import { edit_profile, change_password } from "@/constants/translation/auth.translation";
import * as Yup from "yup";

/**
 * @name EditProfile
 * @description Component for editing user profile and handling form validation using Formik and Yup.
 * 
 * API Response Format:
 * Get Profile: { success, message, data: { user: { id, uuid, full_name, email, profile_photo_url, is_admin, contact_number } } }
 * Update Profile: { success, message, data: { user: { id, uuid, full_name, email, profile_photo_url, is_admin, contact_number } } }
 * 
 * @returns {JSX.Element} The EditProfile component.
 */
const EditProfile = () => {
  const t = useTranslationContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profilePic = useRef();
  const checkLoggenInUser = useSelector(loggedUser);
  
  // Modal states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [emailToVerify, setEmailToVerify] = useState("");
  
  // Email formik
  const emailFormik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: validationHelper.newEmailValidation(t),
    onSubmit: (values) => {
      doSendEmailVerificationOtp({ email: values.email });
    },
  });

  // OTP formik
  const otpFormik = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: validationHelper.newEmailOtpValidation(t),
    onSubmit: (values) => {
      doVerifyEmailOtp({
        otp: values.otp,
        email: emailToVerify,
      });
    },
  });

  const validationSchema = useMemo(
    () => validationHelper.editProfileValidation(t),
    [t]
  );

  const formik = useFormik({
    initialValues: {
      full_name: "",
      contact_number: "",
      profilePhoto: "",
      email: "",
    },
    validationSchema,
    onSubmit: (values) => {
      // Prevent multiple submissions
      if (isLoading) {
        return;
      }
      doUpdateProfile(values);
    },
  });

  /**
   * This api call will gives response of user profile
   * API Returns: { data: { user: { full_name, email, profile_photo_url, contact_number } } }
   */
  const {
    refetch: doGetProfile,
    isLoading: isLoadingProfile,
    data: profile,
  } = useGetProfile(
    (response) => {
    
      
      // API returns: { data: { user: { ... } } }
      const userData = response.data?.user; // Access the nested user object
      if (userData) {
        formik.setValues({
          full_name: userData.full_name || "",
          email: userData.email || "",
          contact_number: userData.contact_number || "",
          profilePhoto: userData.profile_photo_url || "", // Use URL directly as it's already decrypted
        });
      }
    },
    (error) => {
    
      
      const message = error?.response?.data?.message || error?.message || "Failed to load profile";
      toast.error(message);
    }
  );
  
  useEffect(() => {
    // Only set form values if profile data exists and form is not already populated
    if (profile?.data?.user && !formik.values.full_name) {
      const userData = profile.data.user; // Access the nested user object
      formik.setValues({
        full_name: userData.full_name || "",
        email: userData.email || "",
        contact_number: userData.contact_number || "",
        profilePhoto: userData.profile_photo_url || "", // Use URL directly as it's already decrypted
      });
    }
  }, [profile, formik.values.full_name]); // Added formik.values.full_name to prevent re-setting

  // Helper function to get profile image URL for display
  const getProfileImageUrl = () => {
    const previewUrl = imagePreviewFromik(formik.values.profilePhoto);
    return previewUrl || logo;
  };

  // Send email verification OTP mutation
  const { mutate: doSendEmailVerificationOtp, isPending: isSendingOtp } = useSendEmailVerificationOtp(
    (response) => {
      toast.success(response.message);
      setEmailToVerify(emailFormik.values.email);
      setShowEmailModal(false);
      setShowOtpModal(true);
      emailFormik.resetForm();
    },
    (error) => {
      const message = error?.response?.data?.message || error?.message;
      toast.error(message);
    }
  );

  // Verify email OTP mutation
  const { mutate: doVerifyEmailOtp, isPending: isVerifyingOtp } = useVerifyEmailOtp(
    (response) => {
      toast.success(response.message);
      setShowOtpModal(false);
      otpFormik.resetForm();
      // Logout after successful email verification
      doLogout();
    },
    (error) => {
      const message = error?.response?.data?.message || error?.message;
      toast.error(message);
    }
  );

  // Logout mutation
  const { mutate: doLogout } = useLogout(
    () => {
      // Clear user data and redirect to sign-in
      dispatch({ type: 'user/clearUser' });
      navigate(signIn);
    },
    (error) => {
      const message = error?.response?.data?.message || error?.message;
      toast.error(message);
      // Still navigate to sign-in even if logout API fails
      navigate(signIn);
    }
  );

  /**
   * This api call will update user data
   * API Returns: { success, message, data: { user: { ... } } }
   */
  const { mutate: doUpdateProfile, isLoading } = useEditProfile(
    (response) => {
      toast.success(response.message); // Show success message
      
      // API returns: { data: { user: { ... } } }
      const userData = response.data?.user; // Access the nested user object
      if (userData) {
        const dataStore = {
          userData: userData,
        };
        dispatch(updateUserData(dataStore));
      }
      
      doGetProfile(); // Refresh profile data
      navigate(dashboard);
    },
    (error) => {
      const message = error?.response?.data?.message || error?.message;
      toast.error(message);
    }
  );

  // Handle email edit click
  const handleEmailEditClick = () => {
    setShowEmailModal(true);
  };

  // Handle email modal close
  const handleEmailModalClose = () => {
    setShowEmailModal(false);
    emailFormik.resetForm();
  };

  // Handle OTP modal close
  const handleOtpModalClose = () => {
    setShowOtpModal(false);
    otpFormik.resetForm();
    setEmailToVerify("");
  };

  const tabs = [
    {
      label: t(edit_profile.main_heading),
      path: '/edit-profile',
    },
    {
      label: t(change_password.main_heading),
      path: changePassword,
    },
  ]
  return (
    <>
      <CCardGroup>
        <CCard className="p-4 edit-profile-wrapper">
          <TabNavigation tabs={tabs} />
          <div className="text-center">
            <h2 className="fw-bold text-white">{t(edit_profile.main_heading)}</h2>
          </div>
          {!isLoadingProfile && (
            <CForm onSubmit={formik.handleSubmit} className={isLoading ? 'pe-none opacity-50' : ''}>
              <CCardBody>
                <CRow className="position-relative d-flex m-auto mb-3 flex-column image-section">
                  <CCol className="edit-profile-img ">
                    <CImage
                      src={getProfileImageUrl()}
                      alt="profile_img"
                      style={!formik.values.profilePhoto ? { border: '2px solid white', borderRadius: '50%' } : {}}
                    />
                    <div className="upload-profile text-center mb-2">
                      <label
                        htmlFor="profilePhoto"
                        className="edit-profile-image-icon"
                        style={{ cursor: 'pointer' }}
                      >
                        <CIcon icon={cilPencil} />
                      </label>
                      <CFormInput
                        name="profilePhoto"
                        id="profilePhoto"
                        accept=".png, .jpg, .jpeg"
                        onChange={(event) => {
                          const file = event.currentTarget.files[0];
                          const allowedFileTypes = [
                            "image/jpeg",
                            "image/jpg",
                            "image/png",
                          ];
                          const maxSizeInMB = 10; // 🔹 Set max size to 10MB
                          const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

                          if (file) {
                            if (!allowedFileTypes.includes(file.type)) {
                              toast.error(t(edit_profile.invalid_file_format));
                              event.target.value = "";
                              return;
                            }
                            if (file.size > maxSizeInBytes) {
                              toast.error(t(edit_profile.size_of_image));
                              event.target.value = "";
                              return;
                            }
                            formik.setFieldValue("profilePhoto", file);
                          }
                        }}
                        ref={profilePic}
                        style={{ visibility: "hidden", display: "none" }}
                        type={"file"}
                      />
                    </div>
                  </CCol>
                </CRow>
                <CRow>
                  <CCol lg={12} md={12} sm={12} xs={12}>
                    <CustomInput
                      label={t(edit_profile.first_name_label)}
                      type="text"
                      name="full_name"
                      placeholder={t(edit_profile.first_name_placeholder)}
                      value={formik.values.full_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.errors.full_name || ''}
                    />
                  </CCol>
                </CRow>
                <CRow>
                  <CCol lg={6} md={6} sm={6} xs={12}>
                    <CustomInput
                      label={t(edit_profile.email_label)}
                      type="email"
                      name="email"
                      placeholder={t(edit_profile.email_placeholder)}
                      disabled
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.errors.email}
                      onEditClick={handleEmailEditClick}
                    />
                  </CCol>
                  <CCol lg={6} md={6} sm={6} xs={12}>
                    <CustomInput
                      label={t(edit_profile.contact_number_label)}
                      type="phone"
                      name="contact_number"
                      value={formik.values.contact_number}
                      onChange={(value, country) => {
                        formik.setFieldValue("contact_number", value || "");
                      }}
                      placeholder={t(edit_profile.contact_number_placeholder)}
                      onBlur={formik.handleBlur}
                      error={formik.errors.contact_number ? t(formik.errors.contact_number) : ''}
                    />
                  </CCol>
                </CRow>
                <CRow>
                  <CCol className="d-flex justify-content-center mt-2 gap-2">
                    <CButton
                      color="primary"
                      className="px-4"
                      type="submit"
                      disabled={isLoading}
                    >
                      {t(edit_profile.submit_btn_text)}
                    </CButton>
                    <CButton
                      color="secondary"
                      className="px-4"
                      type="button"
                      disabled={isLoading}
                      onClick={() => navigate(dashboard)}
                    >
                      {t(edit_profile.cancel_button)}
                    </CButton>
                  </CCol>
                </CRow>
              </CCardBody>
            </CForm>
          )}
        </CCard>
      </CCardGroup>

      {/* Email Edit Modal */}
      <CModal 
        visible={showEmailModal} 
        onClose={handleEmailModalClose}
        backdrop="static"
        keyboard={false}
        className="email-edit-modal-unique modal-bg-transparent"
        alignment="center"
        aria-labelledby="LiveDemoExampleLabel"
      >
        <CForm onSubmit={emailFormik.handleSubmit}>
          <CModalHeader>
            <CModalTitle id="LiveDemoExampleLabel">
              {t(edit_profile.update_email?.email_heading) || "Update Email"}
            </CModalTitle>
          </CModalHeader>
          <CModalBody>
            <CustomInput
              label={t(edit_profile.update_email?.email_label) || "New Email"}
              type="text"
              name="email"
              placeholder={t(edit_profile.update_email?.email_placeholder) || "Enter your new email"}
              value={emailFormik.values.email}
              onChange={emailFormik.handleChange}
              onBlur={emailFormik.handleBlur}
              error={emailFormik.errors.email}
            />
          </CModalBody>
          <CModalFooter>
            <div className="d-flex align-items-center gap-2">
              <CButton 
                color="primary" 
                type="submit"
                disabled={isSendingOtp || !emailFormik.isValid || !emailFormik.dirty}
              >
                {isSendingOtp && <CSpinner size="sm" variant="grow" />}
                {t(edit_profile.update_email?.submit_btn) || "Send OTP"}
              </CButton>
              <CButton 
                color="secondary" 
                type="button" 
                onClick={handleEmailModalClose}
                disabled={isSendingOtp}
              >
                {t(edit_profile.update_email?.cancel_button) || "Cancel"}
              </CButton>
            </div>
          </CModalFooter>
        </CForm>
      </CModal>

      {/* OTP Verification Modal */}
      <CModal 
        visible={showOtpModal} 
        onClose={handleOtpModalClose}
        backdrop="static"
        keyboard={false}
        className="verify-email-modal modal-bg-transparent"
        alignment="center"
        aria-labelledby="LiveDemoExampleLabel"
      >
        <CForm onSubmit={otpFormik.handleSubmit}>
          <CModalHeader>
            <CModalTitle id="LiveDemoExampleLabel">
              {t(edit_profile.verify_email?.otp_heading) || "Verify OTP"}
            </CModalTitle>
          </CModalHeader>
          <CModalBody>
            <p className="mb-3">
              {t(edit_profile.verify_email?.otp_description) || "Enter the 6-digit OTP sent to"} <strong>{emailToVerify}</strong>
            </p>
            <CustomInput
              label={t(edit_profile.verify_email?.otp_label) || "OTP"}
              type="text"
              name="otp"
              placeholder={t(edit_profile.verify_email?.otp_placeholder) || "Enter 6-digit OTP"}
              value={otpFormik.values.otp}
              onChange={otpFormik.handleChange}
              onBlur={otpFormik.handleBlur}
              error={otpFormik.errors.otp}
              maxLength={6}
            />
            <div className="text-center mt-3">
              <span className="resend-otp" onClick={() => {
                doSendEmailVerificationOtp({ email: emailToVerify });
              }}>
                {t(edit_profile.verify_email?.resend_otp) || "Resend OTP"}
              </span>
            </div>
          </CModalBody>
          <CModalFooter>
            <div className="d-flex align-items-center gap-2">
              <CButton 
                color="primary" 
                type="submit"
                disabled={isVerifyingOtp || !otpFormik.isValid || !otpFormik.dirty}
              >
                {isVerifyingOtp && <CSpinner size="sm" variant="grow" />}
                {t(edit_profile.verify_email?.verify_btn) || "Verify OTP"}
              </CButton>
              <CButton 
                color="secondary" 
                type="button" 
                onClick={handleOtpModalClose}
                disabled={isVerifyingOtp}
              >
                {t(edit_profile.verify_email?.cancel_button) || "Cancel"}
              </CButton>
            </div>
          </CModalFooter>
        </CForm>
      </CModal>
    </>
  );
};

export default EditProfile;
